import { Settings, Clock, BookOpen, FileText } from 'lucide-react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';

import AppLayout from '@/layout/app/index';

import LandingPage from '@/pages/landing';
import TimelinePage from '@/pages/timeline';
import TimelineRawPage from '@/pages/timeline/raw';
import TimelineCuratedPage from '@/pages/timeline/curated';
import TimelineRejectedPage from '@/pages/timeline/rejected';
import CurationPage from '@/pages/curation';
import CurationBoardsPage from '@/pages/curation/boards';
import CurationMetricsPage from '@/pages/curation/metrics';
import { ReportsPage, ReportsListPage, ReportNewPage, ReportDetailLayout, ReportViewRedirect, ReportEditPage, ReportPreviewPage, SendReportPage } from '@/pages/reports/pages';
import LoginPage from '@/pages/login';
import SignupPage from '@/pages/signup';
import SettingsPage from '@/pages/settings';
import NotFoundPage from '@/pages/not-found';
import ProfilePage from '@/pages/settings/account';
import AccountPage from '@/pages/settings/users';
import PartnersPage from '@/pages/settings/partners';
import PartnerClientsPage from '@/pages/settings/partners/clients';
import SourcesPage from '@/pages/settings/sources';
import SourceDetailPage from '@/pages/settings/sources/detail';
import ContentPage from '@/pages/settings/content';
import ContextsPage from '@/pages/settings/content/contexts';
import BrandsSectorsPage from '@/pages/settings/content/brands-sectors';
import CreatorsPage from '@/pages/settings/content/creators';
import ForgotPasswordPage from '@/pages/forgot-password';
import ForgotPasswordSentPage from '@/pages/forgot-password/sent';
import ResetPasswordPage from '@/pages/reset-password';
import PartnerSignupPage from '@/pages/partner-signup';
import PrivacyPage from '@/pages/legal/PrivacyPage';
import TermsPage from '@/pages/legal/TermsPage';
import CookiesPage from '@/pages/legal/CookiesPage';

import { AuthRoute } from '@/components/AuthRoute';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PermissionRoute } from '@/components/PermissionRoute';
import { RootErrorBoundary } from '@/components/RootErrorBoundary';

function TimelineDefaultRedirect() {
  const { user } = useAuth();
  const isRootUser = user?.email === 'root@live.com';
  return <Navigate to={isRootUser ? 'raw' : 'curated'} replace />;
}

export const config = [
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/app/reports" replace />,
      },
      {
        path: 'timeline',
        element: (
          <PermissionRoute route="/app/timeline">
            <TimelinePage />
          </PermissionRoute>
        ),
        icon: <Clock className="w-4 h-4" />,
        name: 'Timeline',
        children: [
          {
            index: true,
            element: <TimelineDefaultRedirect />,
          },
          {
            path: 'raw',
            Component: TimelineRawPage,
            name: 'Timeline Bruta',
          },
          {
            path: 'curated',
            Component: TimelineCuratedPage,
            name: 'Timeline Curada',
          },
          {
            path: 'rejected',
            Component: TimelineRejectedPage,
            name: 'Conteúdos Rejeitados',
          },
        ],
      },
      {
        path: 'curation',
        element: (
          <PermissionRoute route="/app/curation">
            <CurationPage />
          </PermissionRoute>
        ),
        icon: <BookOpen className="w-4 h-4" />,
        name: 'Curadoria',
        children: [
          {
            index: true,
            element: <Navigate to="boards" replace />,
          },
          {
            path: 'boards',
            Component: CurationBoardsPage,
            name: 'Murais',
          },
          {
            path: 'metrics',
            Component: CurationMetricsPage,
            name: 'Métricas',
          },
        ],
      },
      {
        path: 'reports',
        Component: ReportsPage,
        icon: <FileText className="w-4 h-4" />,
        name: 'Reports',
        children: [
          {
            index: true,
            element: (
              <PermissionRoute route="/app/reports">
                <ReportsListPage />
              </PermissionRoute>
            ),
            name: 'Lista de Reports',
          },
          {
            path: 'new',
            element: (
              <PermissionRoute route="/app/reports/new">
                <ReportNewPage />
              </PermissionRoute>
            ),
            name: 'Novo Report',
          },
          {
            path: ':id/edit',
            element: (
              <PermissionRoute route="/app/reports/:id/edit">
                <ReportEditPage />
              </PermissionRoute>
            ),
            name: 'Editar Report',
          },
          {
            path: ':id/view',
            element: <Outlet />,
            name: 'Visualizar report',
            children: [
              {
                index: true,
                element: <ReportDetailLayout />,
              },
              {
                path: 'send',
                name: 'Enviar report',
                element: (
                  <PermissionRoute route="/app/reports">
                    <SendReportPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'settings',
        element: (
          <PermissionRoute route="/app/settings">
            <SettingsPage />
          </PermissionRoute>
        ),
        icon: <Settings className="w-4 h-4" />,
        name: 'Configurações',
        children: [
          {
            path: 'account',
            element: (
              <PermissionRoute route="/app/settings/account">
                <ProfilePage />
              </PermissionRoute>
            ),
            name: 'Minha conta',
          },
          {
            path: 'users',
            element: (
              <PermissionRoute route="/app/settings/users">
                <AccountPage />
              </PermissionRoute>
            ),
            name: 'Usuários',
          },
          {
            path: 'partners/:partnerId/clients',
            element: (
              <PermissionRoute route="/app/settings/partners">
                <PartnerClientsPage />
              </PermissionRoute>
            ),
            name: 'Clientes',
          },
          {
            path: 'partners',
            element: (
              <PermissionRoute route="/app/settings/partners">
                <PartnersPage />
              </PermissionRoute>
            ),
            name: 'Marcas',
          },
          {
            path: 'sources/:id',
            element: (
              <PermissionRoute route="/app/settings/sources">
                <SourceDetailPage />
              </PermissionRoute>
            ),
            name: 'Detalhes da Fonte',
          },
          {
            path: 'sources',
            element: (
              <PermissionRoute route="/app/settings/sources">
                <SourcesPage />
              </PermissionRoute>
            ),
            name: 'Fontes de conteúdo (APIs)',
          },
          {
            path: 'content',
            element: (
              <PermissionRoute route="/app/settings/content">
                <ContentPage />
              </PermissionRoute>
            ),
            name: 'Administração de conteúdos',
            children: [
              {
                path: 'contexts',
                element: (
                  <PermissionRoute route="/app/settings/content/contexts">
                    <ContextsPage />
                  </PermissionRoute>
                ),
                name: 'Contextos Ativáveis',
              },
              {
                path: 'brands-sectors',
                element: (
                  <PermissionRoute route="/app/settings/content/brands-sectors">
                    <BrandsSectorsPage />
                  </PermissionRoute>
                ),
                name: 'Marcas e Setores',
              },
              {
                path: 'creators',
                element: (
                  <PermissionRoute route="/app/settings/content/creators">
                    <CreatorsPage />
                  </PermissionRoute>
                ),
                name: 'Creators Fonte',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: (
      <AuthRoute>
        <LoginPage />
      </AuthRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <AuthRoute>
        <ForgotPasswordPage />
      </AuthRoute>
    ),
  },
  {
    path: '/forgot-password/sent',
    element: (
      <AuthRoute>
        <ForgotPasswordSentPage />
      </AuthRoute>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <AuthRoute>
        <ResetPasswordPage />
      </AuthRoute>
    ),
  },
  {
    path: '/partner/signup',
    element: (
      <AuthRoute>
        <PartnerSignupPage />
      </AuthRoute>
    ),
  },
  {
    path: '/signup',
    element: (
      <AuthRoute>
        <SignupPage />
      </AuthRoute>
    ),
  },
  {
    path: '/app/reports/:id',
    element: (
      <ProtectedRoute>
        <ReportPreviewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reports/:id',
    element: <ReportViewRedirect />,
  },
  {
    path: '/',
    element: <LandingPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/cookies',
    element: <CookiesPage />,
  },
  {
    path: '*',
    Component: NotFoundPage,
  },
]

export const router = createBrowserRouter([
  {
    errorElement: <RootErrorBoundary />,
    children: config,
  },
]); 